// The fleet control groups of both surfaces (arcade.md §7.2, R403; the
// season map, R539).
//
// A control group is a number a player puts one of their own fleets on, so
// the fleet can be selected again with one key. It is the RTS gesture every
// player of the genre already has in their hands, and both surfaces pay for
// it: a five-minute match has no time for a hunt through the water, and a
// season map is too wide for one.
//
// This file is the whole model, and it is pure. It holds no fleet, no
// camera and no DOM: the match (`fast/arcade/match.ts`) and the season map
// (`fast/commands.ts`) own the presses, the two chromes draw the buttons and
// `fast/layers/fleets.ts` draws the badge. Each one is told what this object
// holds and decides nothing itself.
//
// **The two rules of a group, both silent.** A slot holds ONE fleet and a
// fleet holds ONE slot. A press that breaks either one wins: the slot lets
// its old fleet go, the fleet leaves its old slot, and nothing is refused
// and nothing is said. A player who presses a number is stating what that
// number means from now on.
//
// **Who stores it is the one difference between the two surfaces.** A match
// holds its groups in memory alone: a match is one sitting, and a reload
// starts with none. The season map sends every press to the server, which
// stores the groups on the empire and states them in every `Empire` frame;
// `replaceAll` is the door that frame comes through (R539).

import { CONTROL_GROUP_SLOTS } from './gen/balance';

/**
 * How many groups a surface offers: the digits 1 to `CONTROL_GROUP_SLOTS`.
 *
 * The count is one value in `data/contracts/protocol.toml`, and the server
 * refuses a slot outside it (R539). The client must not state a second one.
 */
export const GROUP_SLOTS = CONTROL_GROUP_SLOTS;

/** Is this a slot a player can press? 1 to `GROUP_SLOTS`, and nothing else. */
export function validSlot(slot: number): boolean {
  return Number.isInteger(slot) && slot >= 1 && slot <= GROUP_SLOTS;
}

/** A slot a player can press, and a fleet id that names a fleet. */
function validPair(slot: number, fleetId: number): boolean {
  return validSlot(slot) && Number.isInteger(fleetId) && fleetId > 0;
}

/**
 * The five control groups of one match.
 *
 * Both directions are held, because both are asked for on every frame: the
 * group slots ask which slots are taken, and the badge layer asks which
 * slot every fleet wears. The two maps are written together and can never
 * disagree — every mutator below goes through `wipe`.
 */
export class ControlGroups {
  /** slot → fleet id. A slot with no fleet is absent, never 0. */
  private readonly bySlot = new Map<number, number>();
  /** fleet id → slot. The inverse of the map above, always. */
  private readonly byFleetId = new Map<number, number>();

  /** How many slots hold a fleet. */
  get size(): number { return this.bySlot.size; }

  /**
   * Puts one fleet on one slot, overriding both ways.
   *
   * It answers whether anything moved, so the caller can push the new
   * picture to the layers on a change alone. A press that re-states what
   * the slot already says moves nothing.
   */
  assign(slot: number, fleetId: number): boolean {
    if (!validPair(slot, fleetId) || this.bySlot.get(slot) === fleetId) return false;
    // The slot lets its old fleet go, and the fleet leaves its old slot.
    this.wipeSlot(slot);
    this.wipeFleet(fleetId);
    this.bySlot.set(slot, fleetId);
    this.byFleetId.set(fleetId, slot);
    return true;
  }

  /** The fleet on that slot, or 0 when the slot is free. */
  fleetAt(slot: number): number {
    return this.bySlot.get(slot) ?? 0;
  }

  /** The slot that fleet wears, or 0 when it wears none. */
  slotOf(fleetId: number): number {
    return this.byFleetId.get(fleetId) ?? 0;
  }

  /** Takes one fleet out of its group. Answers whether it was in one. */
  release(fleetId: number): boolean {
    return this.wipeFleet(fleetId);
  }

  /**
   * Keeps the fleets a full frame named and drops every other one.
   *
   * A delta frame names the contacts that went, and a full frame names
   * every contact there is; the match calls this on the second kind, so a
   * group cannot outlive a hull the server has stopped sending. It answers
   * whether anything was dropped.
   */
  keepOnly(alive: ReadonlySet<number>): boolean {
    let changed = false;
    // A Map iterates safely while its current entry is deleted.
    for (const id of this.byFleetId.keys()) {
      if (!alive.has(id)) changed = this.wipeFleet(id) || changed;
    }
    return changed;
  }

  /**
   * Replaces every assignment with the list the server sent (R539).
   *
   * The season map's store is the server: an `Empire` frame states every
   * taken slot, and the client takes that list whole rather than merging it
   * into what it holds. A pair the model refuses is dropped, so a bad slot
   * cannot enter the maps. It answers whether the picture changed, so the
   * caller pushes to the layers on a change alone.
   */
  replaceAll(pairs: readonly { slot: number; fleetId: number }[]): boolean {
    let changed = false;
    const want = new Map<number, number>();
    for (const p of pairs) {
      if (validPair(p.slot, p.fleetId)) want.set(p.slot, p.fleetId);
    }
    for (const slot of this.bySlot.keys()) {
      if (!want.has(slot)) changed = this.wipeSlot(slot) || changed;
    }
    for (const [slot, fleetId] of want) changed = this.assign(slot, fleetId) || changed;
    return changed;
  }

  /** The slots that hold a fleet, ascending. */
  slots(): number[] {
    return [...this.bySlot.keys()].sort((a, b) => a - b);
  }

  /** Every assignment as fleet id → slot, for the badge layer. */
  byFleet(): ReadonlyMap<number, number> {
    return this.byFleetId;
  }

  /** Empties one slot, both ways. */
  private wipeSlot(slot: number): boolean {
    const held = this.bySlot.get(slot);
    if (held === undefined) return false;
    this.bySlot.delete(slot);
    this.byFleetId.delete(held);
    return true;
  }

  /** Takes one fleet off whatever slot it holds, both ways. */
  private wipeFleet(fleetId: number): boolean {
    const slot = this.byFleetId.get(fleetId);
    if (slot === undefined) return false;
    this.byFleetId.delete(fleetId);
    this.bySlot.delete(slot);
    return true;
  }
}

// --- the assign by hold and its UNDO (R1817) -------------------------------

/**
 * What one assign changes, read before it lands: the slot, the fleet, the
 * fleet that LEFT the slot (0 when the slot was free or held this fleet),
 * and the slot the fleet came FROM (0 when it held none, or this one).
 *
 * The long press of a group slot reads it to name the fleet that left in
 * its toast, and UNDO turns it back (085 card 13, open point 7).
 */
export interface AssignPlan {
  slot: number;
  fleetId: number;
  left: number;
  from: number;
}

/** The plan of putting `fleetId` on `slot`, against the groups held now. */
export function planAssign(g: ControlGroups, slot: number, fleetId: number): AssignPlan {
  const held = g.fleetAt(slot);
  const was = g.slotOf(fleetId);
  return { slot, fleetId, left: held !== fleetId ? held : 0, from: was !== slot ? was : 0 };
}

/**
 * The assigns that turn a plan back, in their order. The fleet that left
 * goes back on its slot first, which takes the moved fleet off it; then
 * the moved fleet goes back on the slot it came from. Each step is one
 * plain assign, so the server takes UNDO through the one intent it knows.
 */
export function undoSteps(p: AssignPlan): { slot: number; fleetId: number }[] {
  const out: { slot: number; fleetId: number }[] = [];
  if (p.left) out.push({ slot: p.slot, fleetId: p.left });
  if (p.from) out.push({ slot: p.from, fleetId: p.fleetId });
  return out;
}
